import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { UPDATE } from "../components/constants/request-type";
import { service_api } from "../components/constants/url";
import { openNotificationWithIcon } from "../components/elements/notification-bar";
import { MakeRequestAsync } from "../components/functions/axios";
import { uploadSecureFile } from "../components/functions/upload-file";
import { useCertificateStatus } from "../hooks/useCertificateStatus";
import { POST_USER_DATA } from "../store/action";
import { t } from "../translations";
import { createLogger } from "../utils/logger";

const logger = createLogger("FoodHygieneCertificate");

const FoodHygieneCertificate = () => {
  const user = useSelector((state) => state.user);
  const lang = useSelector((state) => state.lang);
  const history = useHistory();
  const dispatch = useDispatch();
  const { status } = useCertificateStatus();

  // Note: Certificate status assumes 'approved' when user is visible
  // This is temporary logic - visible users are considered certified

  const [profil, setProfil] = useState({
    preview: user?.preview,
    pic1: user?.pic1,
    pic2: user?.pic2,
    firstname: user?.firstname,
    lastname: user?.lastname,
    email: user?.email,
    number: user?.number,
    bio: user?.bio,
    notification: user?.notification,
    notification_cancel: user?.notification_cancel,
    order_timing: user?.order_timing,
    country: user?.country,
    geographical_tag: user?.geographical_tag,
    certificate_pic: user?.certificate_safety?.pic,
    certificate_exp: user?.certificate_safety?.expirity,
    pickup_time: user?.pickup_time,
    availability: user?.availability,
    few_days: user?.few_days,
    planned_days: user?.planned_days
  });

  const [certifPic, setCertifPic] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const certifPicInputRef = useRef(null);

  // File upload handlers
  const handleButtonClick = () => {
    return certifPicInputRef.current.click();
  };

  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf"
    ];

    if (!file) {
      throw new Error("No file selected");
    }

    if (file.size > maxSize) {
      throw new Error("File size must be less than 5MB");
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error("Only JPEG, PNG, and PDF files are allowed");
    }

    return true;
  };

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setUploading(true);
    setImageError(false);
    setSelectedFile(selectedFile);

    try {
      // Validate file before upload
      validateFile(selectedFile);

      // Only read as URL for images, not PDFs
      if (selectedFile.type.startsWith("image/")) {
        readFileAsUrl(selectedFile);
      }

      if (!user?.refresh_token) {
        throw new Error("Authentication required. Please log in again.");
      }

      const uploadResponse = await uploadSecureFile(
        selectedFile,
        user?.refresh_token
      );

      if (uploadResponse?.data?.success) {
        setProfil({
          ...profil,
          certificate_pic: uploadResponse.data.url
        });

        openNotificationWithIcon(
          "success",
          t("select_profil.certificate_uploaded_successfully", lang)
        );
      } else {
        throw new Error(uploadResponse?.data?.message || "Upload failed");
      }
    } catch (error) {
      const errorMessage =
        error.message || t("select_profil.certificate_upload_error", lang);

      openNotificationWithIcon("error", errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const readFileAsUrl = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCertifPic(reader.result);
    };
    reader.onerror = () => {
      setImageError(true);
    };
    return reader.readAsDataURL(file);
  };

  // Helper function to determine file type
  const getFileType = (file) => {
    if (file?.type) {
      if (file.type.startsWith("image/")) return "image";
      if (file.type === "application/pdf") return "pdf";
    }
    return "unknown";
  };

  // Helper function to get file name
  const getFileName = (url) => {
    if (url && typeof url === "string") {
      return url.split("/").pop() || "certificate";
    }
    return selectedFile?.name || "certificate";
  };

  const handleSave = async () => {
    if (!profil.certificate_pic) {
      openNotificationWithIcon(
        "warning",
        t("select_profil.please_upload_certificate", lang)
      );
      return;
    }

    if (!profil.certificate_exp) {
      openNotificationWithIcon(
        "warning",
        t("select_profil.please_select_expiration_date", lang)
      );
      return;
    }

    // Check if expiration date is in the future
    const expirationDate = new Date(profil.certificate_exp);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    if (expirationDate <= today) {
      openNotificationWithIcon(
        "warning",
        t("select_profil.expiration_date_must_be_future", lang)
      );
      return;
    }

    try {
      // Debug: Check user data
      logger.log("User data:", {
        id: user?._id,
        hasToken: !!user?.refresh_token,
        certificatePic: profil.certificate_pic,
        certificateExp: profil.certificate_exp
      });

      if (!user?._id) {
        throw new Error("User ID not found. Please log in again.");
      }

      const request_details = {
        type: UPDATE,
        url: service_api,
        route: `user/update-user/${user._id}`,
        data: {
          certificate_safety: {
            pic: profil.certificate_pic,
            expirity: profil.certificate_exp
          }
        },
        token: user?.refresh_token
      };

      logger.log(
        "Making API request to:",
        `${request_details.url}/${request_details.route}`
      );
      const response = await MakeRequestAsync(request_details);

      if (response?.data?.message === "User updated successfully") {
        // Update user data in Redux store
        dispatch({
          type: POST_USER_DATA,
          data: {
            ...user,
            certificate_safety: {
              pic: profil.certificate_pic,
              expirity: profil.certificate_exp
            }
          }
        });

        openNotificationWithIcon(
          "success",
          t("select_profil.certificate_saved_successfully", lang)
        );

        // Navigate back to previous page after successful save
        setTimeout(() => {
          history.goBack();
        }, 1500);
      } else {
        throw new Error(response?.data?.message || "Save failed");
      }
    } catch (error) {
      logger.error("Save certificate error:", error);

      let errorMessage = t("select_profil.certificate_save_error", lang);

      // Handle specific error cases
      if (error?.response?.status === 404) {
        errorMessage = t("select_profil.user_not_found_error", lang);
      } else if (error?.response?.status === 401) {
        errorMessage = t("select_profil.authentication_error", lang);
      } else if (error?.message) {
        errorMessage = error.message;
      }

      openNotificationWithIcon("error", errorMessage);
    }
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Main Certificate Management Section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "30px",
          marginBottom: "30px"
        }}
      >
        {/* Left Column - Certificate Upload */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "2px solid #e3f2fd",
            borderRadius: "15px",
            padding: "25px"
          }}
        >
          <h3
            style={{
              margin: "0 0 20px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1976d2",
              textAlign: "center"
            }}
          >
            {t("select_profil.certificate_choice_title", lang)}
          </h3>

          <p
            style={{
              margin: "0 0 20px 0",
              fontSize: "14px",
              color: "#666",
              textAlign: "center"
            }}
          >
            {t("select_profil.certificate_choice_description", lang)}
          </p>

          {/* Option 1: Get New Certificate */}
          <div
            style={{
              border: "2px solid #e3f2fd",
              borderRadius: "15px",
              padding: "20px",
              backgroundColor: "#f8f9ff",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              marginBottom: "15px"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "#1976d2";
              e.currentTarget.style.backgroundColor = "#e3f2fd";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "#e3f2fd";
              e.currentTarget.style.backgroundColor = "#f8f9ff";
            }}
            onClick={() => history.push("/panichefs-academy")}
          >
            <div style={{ fontSize: "40px", marginBottom: "15px" }}>🎓</div>
            <h4
              style={{
                margin: "0 0 10px 0",
                fontSize: "16",
                fontWeight: "600",
                color: "#1976d2"
              }}
            >
              {t("select_profil.get_new_certificate", lang)}
            </h4>
            <p
              style={{
                margin: "0 0 15px 0",
                fontSize: "13px",
                color: "#666",
                lineHeight: "1.4"
              }}
            >
              {t("select_profil.take_online_training", lang)}
            </p>
            <button
              style={{
                backgroundColor: "#2196f3",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#1976d2";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "#2196f3";
              }}
            >
              {t("select_profil.start_training", lang)}
            </button>
          </div>

          {/* Option 2: Upload Existing Certificate */}
          <div
            style={{
              border: "2px solid #e8f5e8",
              borderRadius: "15px",
              padding: "20px",
              backgroundColor: "#f8fff8",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "#4caf50";
              e.currentTarget.style.backgroundColor = "#e8f5e8";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "#e8f5e8";
              e.currentTarget.style.backgroundColor = "#f8fff8";
            }}
            onClick={handleButtonClick}
          >
            <div style={{ fontSize: "40px", marginBottom: "15px" }}>📄</div>
            <h4
              style={{
                margin: "0 0 10px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: "#388e3c"
              }}
            >
              {t("select_profil.upload_existing_certificate", lang)}
            </h4>
            <p
              style={{
                margin: "0 0 15px 0",
                fontSize: "13px",
                color: "#666",
                lineHeight: "1.4"
              }}
            >
              {t("select_profil.already_have_certificate", lang)}
            </p>
            <button
              disabled={uploading}
              style={{
                backgroundColor: uploading ? "#ccc" : "#4caf50",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: uploading ? "not-allowed" : "pointer",
                transition: "all 0.3s ease"
              }}
              onMouseOver={(e) => {
                if (!uploading) {
                  e.target.style.backgroundColor = "#388e3c";
                }
              }}
              onMouseOut={(e) => {
                if (!uploading) {
                  e.target.style.backgroundColor = "#4caf50";
                }
              }}
            >
              {uploading
                ? "UPLOADING..."
                : t("select_profil.upload_file", lang)}
            </button>
          </div>

          {/* Hidden file input */}
          <input
            type="file"
            ref={certifPicInputRef}
            style={{ display: "none" }}
            accept="image/*,.pdf"
            onChange={handleFileSelect}
          />
        </div>

        {/* Right Column - Certificate Preview & Details */}
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: "15px",
            padding: "25px"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "20px"
            }}
          >
            <h3
              style={{
                margin: "0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#333"
              }}
            >
              {t("select_profil.certificate_details", lang)}
            </h3>

            {status === "approved" && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span style={{ fontSize: "16px" }}>✅</span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#4caf50",
                    fontWeight: "600",
                    backgroundColor: "#e8f5e8",
                    padding: "4px 8px",
                    borderRadius: "12px"
                  }}
                >
                  {t("select_profil.valid_certificate", lang)}
                </span>
              </div>
            )}
          </div>

          {/* Certificate Preview */}
          {(profil?.certificate_pic || certifPic || selectedFile) && (
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: "10px",
                padding: "15px",
                backgroundColor: "#f9f9f9",
                marginBottom: "20px"
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  margin: "0 0 10px 0",
                  color: "#333"
                }}
              >
                {t("select_profil.certificate_preview", lang)}
              </p>

              {/* File Info */}
              <div
                style={{
                  marginBottom: "10px",
                  padding: "8px 12px",
                  backgroundColor: "#e8f4fd",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#1976d2"
                }}
              >
                📄 {getFileName(profil?.certificate_pic || certifPic)}
              </div>

              {/* Preview Content */}
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  minHeight: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {(() => {
                  const currentFile =
                    selectedFile ||
                    (profil?.certificate_pic && { type: "uploaded" });
                  const fileType = getFileType(currentFile);
                  const imageSrc = profil?.certificate_pic || certifPic;

                  if (fileType === "image" && imageSrc && !imageError) {
                    return (
                      <img
                        src={imageSrc}
                        alt="certificate-preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "200px",
                          borderRadius: "6px",
                          objectFit: "contain"
                        }}
                        onError={() => setImageError(true)}
                      />
                    );
                  } else if (
                    fileType === "pdf" ||
                    (imageSrc && imageSrc.includes(".pdf"))
                  ) {
                    return (
                      <div>
                        <div
                          style={{
                            width: "100%",
                            height: "300px",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            overflow: "hidden",
                            marginBottom: "10px"
                          }}
                        >
                          <iframe
                            src={imageSrc}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none"
                            }}
                            title="PDF Preview"
                            onError={() => {
                              // Fallback if PDF can't be displayed in iframe
                              setImageError(true);
                            }}
                          />
                        </div>
                        <button
                          onClick={() => window.open(imageSrc, "_blank")}
                          style={{
                            backgroundColor: "#4caf50",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            width: "100%"
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = "#45a049";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = "#4caf50";
                          }}
                        >
                          {t("select_profil.view_pdf_in_new_tab", lang)}
                        </button>
                      </div>
                    );
                  } else if (imageError) {
                    // Check if it's a PDF that failed to load
                    const isPdfError =
                      fileType === "pdf" ||
                      (imageSrc && imageSrc.includes(".pdf"));

                    return (
                      <div
                        style={{
                          textAlign: "center",
                          color: "#f44336",
                          padding: "20px"
                        }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "10px" }}>
                          {isPdfError ? "📄" : "⚠️"}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            marginBottom: "10px"
                          }}
                        >
                          {isPdfError
                            ? "PDF Preview Unavailable"
                            : "Preview Error"}
                        </div>
                        <div style={{ fontSize: "12px", marginBottom: "15px" }}>
                          {isPdfError
                            ? t("select_profil.pdf_preview_unavailable", lang)
                            : t("select_profil.image_preview_error", lang)}
                        </div>
                        {isPdfError && (
                          <button
                            onClick={() => window.open(imageSrc, "_blank")}
                            style={{
                              backgroundColor: "#1976d2",
                              color: "white",
                              border: "none",
                              padding: "8px 16px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.3s ease"
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = "#1565c0";
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = "#1976d2";
                            }}
                          >
                            {t("select_profil.view_pdf_in_new_tab", lang)}
                          </button>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <div
                        style={{
                          textAlign: "center",
                          color: "#666"
                        }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "10px" }}>
                          📁
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: "600" }}>
                          File Uploaded
                        </div>
                        <div style={{ fontSize: "12px", marginTop: "5px" }}>
                          {t("select_profil.file_uploaded_successfully", lang)}
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          {/* Expiration Date Input */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#333"
              }}
            >
              {t("select_profil.expiration_date", lang)}
            </label>
            <input
              style={{
                padding: "12px",
                borderRadius: "10px",
                height: "45px",
                border: "1px solid #ddd",
                width: "100%",
                fontSize: "14px",
                outline: "none",
                transition: "all 0.3s ease"
              }}
              placeholder={t("select_profil.expiration_placeholder", lang)}
              type="date"
              name="certificate_exp"
              value={profil?.certificate_exp || ""}
              onChange={(e) => {
                setProfil({
                  ...profil,
                  certificate_exp: e.target.value
                });
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#ff114d";
                e.target.style.backgroundColor = "#fff";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#ddd";
                e.target.style.backgroundColor = "#f8f9fa";
              }}
            />
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            style={{
              backgroundColor: "#ff114d",
              color: "white",
              border: "none",
              padding: "15px 30px",
              borderRadius: "10px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
              width: "100%",
              transition: "all 0.3s ease"
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "#e00e3d";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "#ff114d";
            }}
          >
            {t("select_profil.save", lang)}
          </button>
        </div>
      </div>

      {/* Information Section */}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "10px",
          padding: "20px"
        }}
      >
        <h3
          style={{ margin: "0 0 15px 0", color: "#333", textAlign: "center" }}
        >
          {t("select_profil.certificate_description", lang)}
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px"
          }}
        >
          <div>
            <h4 style={{ color: "#666", margin: "0 0 10px 0" }}>
              📋 {t("select_profil.requirements", lang)}
            </h4>
            <ul style={{ margin: 0, paddingLeft: "20px", color: "#666" }}>
              <li>{t("select_profil.valid_food_safety_certificate", lang)}</li>
              <li>{t("select_profil.certificate_not_expired", lang)}</li>
              <li>{t("select_profil.clear_readable_document", lang)}</li>
            </ul>
          </div>

          <div>
            <h4 style={{ color: "#666", margin: "0 0 10px 0" }}>
              ⏰ {t("select_profil.timeline", lang)}
            </h4>
            <ul style={{ margin: 0, paddingLeft: "20px", color: "#666" }}>
              <li>{t("select_profil.required_within_30_days", lang)}</li>
              <li>{t("select_profil.verification_takes_24_48_hours", lang)}</li>
              <li>{t("select_profil.certificate_valid_5_years", lang)}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodHygieneCertificate;
